import { cn } from "@win-reality/react-ui";

import {
  formatMph,
  formatRunsOuts,
  PitchSummary as PitchSummaryData,
  RunsOuts,
} from "../../utils/plateAppearanceFeed";
import { PitchStatus } from "../../utils/tagQueue";

type PitchSummaryProps = {
  summary: PitchSummaryData;
  omnibusEventId: string;
  status?: PitchStatus;
  /** Says why the row is locked, in place of the detail lines */
  statusLabel: string | null;
  onAddDetails?: () => void;
};

/** Title and detail lines of a pitch row. */
export const PitchSummary = ({
  summary,
  omnibusEventId,
  status,
  statusLabel,
  onAddDetails,
}: PitchSummaryProps) => {
  const isUntagged =
    summary.kind === "untaggedHit" || summary.kind === "untaggedPitch";

  if (isUntagged && statusLabel) {
    return <SaveStatus status={status} label={statusLabel} />;
  }

  return (
    <>
      {isUntagged ? (
        <UntaggedButton
          omnibusEventId={omnibusEventId}
          onAddDetails={onAddDetails}
        />
      ) : (
        <div
          data-testid="plate-appearance-pitch-result-value"
          data-value={
            summary.kind === "inPlay" ? summary.playResult : summary.result
          }
          className="truncate text-gray-900 dark:text-white"
        >
          {summary.kind === "inPlay" ? summary.playResult : summary.result}
        </div>
      )}
      {statusLabel ? (
        <SaveStatus status={status} label={statusLabel} />
      ) : (
        <PitchDetails summary={summary} />
      )}
    </>
  );
};

const PitchDetails = ({ summary }: { summary: PitchSummaryData }) => {
  switch (summary.kind) {
    case "untaggedHit":
    case "inPlay":
      return (
        <>
          {summary.contactType && (
            <div
              data-testid="plate-appearance-contact-type-value"
              data-value={summary.contactType}
              className="truncate text-tagging-muted"
            >
              {summary.contactType}
            </div>
          )}
          {summary.exitVelocity != null && (
            <div
              data-testid="plate-appearance-exit-speed-value"
              data-value={summary.exitVelocity}
              className="truncate text-tagging-muted"
            >
              Exit Velo {formatMph(summary.exitVelocity)} MPH
            </div>
          )}
        </>
      );
    case "untaggedPitch":
    case "pitch":
      return (
        <PitchTypeAndSpeed
          pitchType={summary.pitchType}
          pitchVelocity={summary.pitchVelocity}
        />
      );
  }
};

/** "Sinker 93 MPH" */
const PitchTypeAndSpeed = ({
  pitchType,
  pitchVelocity,
}: {
  pitchType: string | null;
  pitchVelocity: number | null;
}) => {
  if (!pitchType && pitchVelocity == null) return null;

  return (
    <div className="truncate text-tagging-muted">
      {pitchType && (
        <span
          data-testid="plate-appearance-pitch-type-value"
          data-value={pitchType}
        >
          {pitchType}
        </span>
      )}
      {pitchType && pitchVelocity != null && " "}
      {pitchVelocity != null && (
        <span
          data-testid="plate-appearance-release-speed-value"
          data-value={pitchVelocity}
        >
          {formatMph(pitchVelocity)} MPH
        </span>
      )}
    </div>
  );
};

/**
 * Right side of a pitch row: the count, or the runs and outs when the pitch
 * ended the at bat. Null when there is neither.
 */
export const getPitchOutcome = (summary: PitchSummaryData) => {
  if (summary.kind === "untaggedHit" || summary.kind === "untaggedPitch") {
    return null;
  }
  if (summary.runsOuts) return <RunsOutsLines runsOuts={summary.runsOuts} />;
  if (summary.kind === "pitch" && summary.count) {
    return (
      <span
        data-testid="plate-appearance-count-value"
        data-value={summary.count}
      >
        {summary.count}
      </span>
    );
  }
  return null;
};

const RunsOutsLines = ({ runsOuts }: { runsOuts: RunsOuts }) => {
  const lines = formatRunsOuts(runsOuts);
  if (!lines) return null;

  return (
    <div
      data-testid="plate-appearance-runs-outs-value"
      data-runs={runsOuts.runs}
      data-outs={runsOuts.outs}
      // Narrow so "2 runs scored" wraps onto two lines like "1 out / 1 run"
      className="max-w-20 text-right"
    >
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
};

const SaveStatus = ({
  status,
  label,
}: {
  status?: PitchStatus;
  label: string;
}) => (
  <div
    data-testid="plate-appearance-pitch-save-status"
    data-status={status}
    className="truncate text-tagging-muted"
  >
    {label}
  </div>
);

const UntaggedButton = ({
  omnibusEventId,
  onAddDetails,
}: {
  omnibusEventId: string;
  onAddDetails?: () => void;
}) => (
  <button
    type="button"
    data-testid="plate-appearance-add-details-button"
    data-event-uuid={omnibusEventId}
    onClick={(e) => {
      // The row's own click would select the pitch a second time
      e.stopPropagation();
      onAddDetails?.();
    }}
    className={cn(
      // Text
      "text-left text-tagging-accent underline",
      // States
      "hover:no-underline focus:outline-none",
    )}
  >
    Untagged
  </button>
);
